/**
 * ================================================================
 * API SERVICE: MandateService
 * ================================================================
 *
 * @service          mandateApi
 * @description      API service for managing E-Mandate (Online and Physical)
 * @baseUrl          myaccountLive for mandate operations, profile for bank details
 *
 * @endpoints
 * - GET    /profile/bank-details - Fetch user bank accounts
 * - POST   /online - Submit online e-mandate and get HDFC redirect data
 * - POST   /physical - Generate physical e-mandate PDF
 *
 * @authentication  AuthToken header required for all endpoints
 * @rateLimit       100 requests per minute per user
 *
 * @caching
 * - Strategy: Cache for bank details, no caching for mutations
 * - Tags: ['Mandate', 'BankDetails'] for cache invalidation
 *
 * @errorHandling
 * - Network errors: Automatic retry with exponential backoff
 * - Auth errors: Redirect to login page
 * - Validation errors: Display user-friendly messages
 *
 * @types
 * - Imported from @/types/mandate
 * - BankDetailsResponse: Bank account list response
 * - OnlineEMandateResponse: HDFC form data response
 * - SubmitOnlineEMandatePayload: Online submission request
 * - SubmitPhysicalEMandatePayload: Physical PDF request
 *
 * ================================================================
 */

import { createApi } from '@reduxjs/toolkit/query/react';
import { prepareAuthHeaders } from '@/lib/auth/tokenUtils';
import { createBaseQuery } from '@/lib/api/baseConfig';
import type {
  BankDetailsResponse,
  OnlineEMandateResponse,
  SubmitOnlineEMandatePayload,
  SubmitPhysicalEMandatePayload,
} from '@/types/mandate';

export const mandateApi = createApi({
  reducerPath: 'mandateApi',
  baseQuery: createBaseQuery('connectv2'),
  tagTypes: ['Mandate', 'BankDetails'],
  endpoints: (builder) => ({
    // ================================================================
    // QUERY ENDPOINTS (READ OPERATIONS)
    // ================================================================

    /**
     * Fetch bank details for the authenticated user
     *
     * NOTE: This endpoint uses a different base URL (profile) than other endpoints.
     * We override the base URL by providing the full URL in the query.
     *
     * @endpoint GET /profile/bank-details
     * @param {string} clientCode - Client identification code
     * @returns {BankDetailsResponse} List of user's bank accounts
     * @cache Provides 'BankDetails' tag for invalidation
     */
    getBankDetails: builder.query<BankDetailsResponse, string>({
      query: (clientCode) => ({
        url: `/v2/profile/bank-details`,
        method: 'GET',
        params: { source: 'ap', clientCode },
      }),
      providesTags: ['BankDetails'],
    }),

    // ================================================================
    // MUTATION ENDPOINTS (WRITE OPERATIONS)
    // ================================================================

    /**
     * Submit online e-mandate and receive HDFC redirect data
     *
     * @endpoint POST /v1/account_profile/e_mandate/online
     * @param {Object} params - Submission parameters
     * @param {SubmitOnlineEMandatePayload} params.body - Mandate submission data
     * @param {string} params.clientCode - Client identification code
     * @returns {OnlineEMandateResponse} HDFC form data for redirect
     * @invalidates 'Mandate' tag to refresh related queries
     */
    submitOnlineEMandate: builder.mutation<
      OnlineEMandateResponse,
      { body: SubmitOnlineEMandatePayload; clientCode: string }
    >({
      query: ({ body, clientCode }) => ({
        url: `/v2/mandate/e-mandate/online`,
        method: 'POST',
        body,
        params: { source: 'ap', clientCode },
      }),
      invalidatesTags: ['Mandate'],
    }),

    /**
     * Generate physical e-mandate PDF for download
     *
     * @endpoint POST /v1/account_profile/e_mandate/physical
     * @param {Object} params - Generation parameters
     * @param {SubmitPhysicalEMandatePayload} params.body - PDF generation data
     * @param {string} params.clientCode - Client identification code
     * @returns {Blob} PDF file blob for download
     * @invalidates 'Mandate' tag to refresh related queries
     */
    submitPhysicalEMandate: builder.mutation<
      Blob,
      { body: SubmitPhysicalEMandatePayload; clientCode: string }
    >({
      query: ({ body, clientCode }) => ({
        url: `/v2/mandate/e-mandate/physical`,
        method: 'POST',
        body,
        params: { source: 'ap', clientCode },
        responseHandler: async (response) => {
          // ================================================================
          // BLOB RESPONSE HANDLING
          // ================================================================
          //
          // Purpose: Convert PDF response to Blob for file download
          // Error handling: Check content type before blob conversion
          // ================================================================

          if (!response.ok) {
            throw new Error('Failed to generate PDF');
          }

          const blob = await response.blob();

          // Verify it's actually a PDF
          if (!blob.type.includes('pdf') && !blob.type.includes('application/octet-stream')) {
            throw new Error('Invalid response format - expected PDF');
          }

          return blob;
        },
      }),
      invalidatesTags: ['Mandate'],
    }),
  }),
});

// ================================================================
// EXPORTED HOOKS
// ================================================================
//
// Auto-generated hooks for use in React components
// Naming convention: use[EndpointName][Query|Mutation]
// ================================================================

export const {
  useGetBankDetailsQuery,
  useSubmitOnlineEMandateMutation,
  useSubmitPhysicalEMandateMutation,
} = mandateApi;

/**
 * ================================================================
 * END OF SERVICE: MandateService
 * ================================================================
 *
 * @exports          mandateApi, hooks for React components
 * @dependencies     RTK Query, prepareAuthHeaders, TypeScript types
 * @usedBy           E-Mandate components and pages
 *
 * @maintenance
 * - Cache strategy: Review if query endpoints are added
 * - Error handling: Monitor API error rates
 * - Performance: Track mutation response times
 *
 * @notes
 * - getBankDetails uses full URL override for different base
 * - Online/Physical endpoints use myaccountLive base
 *
 * ================================================================
 */
